/**
 * UPZIO — Dashboard complet v2.0
 * React + Supabase + Claude API
 * Admin (France) + Portail client
 *
 * INSTALLATION :
 *   npm install @supabase/supabase-js
 *
 * VARIABLES D'ENVIRONNEMENT (.env) :
 *   REACT_APP_SUPABASE_URL=https://fvauvssllcjvyzaxjyny.supabase.co
 *   REACT_APP_SUPABASE_KEY=votre_clé_anon
 *   REACT_APP_API_URL=https://upzio-backend-production.up.railway.app
 *
 * ROUTING (nginx app.upzio.fr) :
 *   /login        → LoginScreen
 *   /admin/*      → AdminDash  (role = admin)
 *   /dashboard/*  → ClientPortal (role = client)
 */

import { useState, useEffect, useCallback } from "react";
import { createClient } from "@supabase/supabase-js";

// ─── Supabase ────────────────────────────────────────────────────────────────
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL || "",
  process.env.REACT_APP_SUPABASE_KEY || ""
);

const API = process.env.REACT_APP_API_URL || "https://upzio-backend-production.up.railway.app";

// ─── Design tokens Upzio ─────────────────────────────────────────────────────
const C = {
  olive:   "#1e3a0f",
  olive2:  "#2d5a18",
  gold:    "#c8921a",
  gold2:   "#e8b040",
  cream:   "#faf7f0",
  bg:      "#f5f3ee",
  border:  "#e0ddd6",
  muted:   "#888780",
  green:   "#1D9E75",
  greenBg: "#EAF3DE",
  greenTxt:"#27500A",
  amber:   "#BA7517",
  amberBg: "#FAEEDA",
  amberTxt:"#633806",
  red:     "#A32D2D",
  redBg:   "#FCEBEB",
  redTxt:  "#791F1F",
  blueBg:  "#E6F1FB",
  blueTxt: "#0C447C",
};

// ─── Styles inline partagés ──────────────────────────────────────────────────
const S = {
  card: {
    background: "#fff",
    border: `0.5px solid ${C.border}`,
    borderRadius: 10,
    padding: "14px 16px",
    marginBottom: 12,
  },
  badge: (bg, txt) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    padding: "3px 9px",
    borderRadius: 20,
    fontWeight: 500,
    background: bg,
    color: txt,
  }),
  btn: (variant = "default") => {
    const variants = {
      default: { background: "#fff", color: C.olive, border: `0.5px solid ${C.border}` },
      gold:    { background: C.gold, color: "#fff", border: `0.5px solid ${C.gold}` },
      danger:  { background: "#fff", color: C.red, border: `0.5px solid #F09595` },
      ghost:   { background: "transparent", color: C.muted, border: "none" },
    };
    return {
      fontFamily: "'DM Sans', sans-serif",
      fontSize: 12,
      padding: "7px 14px",
      borderRadius: 6,
      cursor: "pointer",
      transition: "all .15s",
      ...variants[variant],
    };
  },
  input: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 13,
    padding: "7px 12px",
    border: `0.5px solid ${C.border}`,
    borderRadius: 6,
    background: "#fff",
    color: C.olive,
    outline: "none",
    width: "100%",
  },
  label: {
    fontSize: 11,
    color: C.muted,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: 4,
    display: "block",
  },
  sectionTitle: {
    fontFamily: "'Syne', sans-serif",
    fontSize: 14,
    fontWeight: 500,
    color: C.olive,
    marginBottom: 10,
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const stars = (n) => "★".repeat(n) + "☆".repeat(5 - n);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "—";
const fmtMoney = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")}€`;

function Badge({ children, variant = "default" }) {
  const map = {
    default:   [C.border, C.muted],
    active:    [C.greenBg, C.greenTxt],
    suspended: [C.redBg, C.redTxt],
    premium:   [C.amberBg, C.amberTxt],
    essential: ["#F1EFE8", "#5F5E5A"],
    published: [C.greenBg, C.greenTxt],
    scheduled: [C.blueBg, C.blueTxt],
    pending:   [C.amberBg, C.amberTxt],
    error:     [C.redBg, C.redTxt],
    negative:  [C.redBg, C.red],
  };
  const [bg, txt] = map[variant] || map.default;
  return <span style={S.badge(bg, txt)}>{children}</span>;
}

function Btn({ children, variant, onClick, style, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{ ...S.btn(variant), opacity: disabled ? 0.5 : 1, ...style }}
    >
      {children}
    </button>
  );
}

function KpiCard({ label, value, sub, subColor }) {
  return (
    <div style={{
      background: "#fff",
      border: `0.5px solid ${C.border}`,
      borderRadius: 8,
      padding: "10px 14px",
    }}>
      <div style={S.label}>{label}</div>
      <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 500, color: C.olive }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: subColor || C.green, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <label style={{ position: "relative", display: "inline-block", width: 36, height: 20, cursor: "pointer" }} aria-label={label}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        style={{ opacity: 0, width: 0, height: 0 }} />
      <span style={{
        position: "absolute", inset: 0,
        background: checked ? C.olive : "#d0cdc5",
        borderRadius: 20, transition: ".2s",
      }}>
        <span style={{
          position: "absolute",
          width: 14, height: 14,
          left: checked ? 19 : 3, bottom: 3,
          background: "#fff", borderRadius: "50%", transition: ".2s",
        }} />
      </span>
    </label>
  );
}

function Spinner() {
  return <div style={{ textAlign: "center", padding: 40, color: C.muted, fontSize: 13 }}>Chargement…</div>;
}

// ─── Hook Supabase générique ──────────────────────────────────────────────────
function useSupabase(table, options = {}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    let q = supabase.from(table).select(options.select || "*");
    if (options.filter) options.filter.forEach(([col, val]) => { q = q.eq(col, val); });
    if (options.order) q = q.order(options.order, { ascending: options.asc ?? false });
    if (options.limit) q = q.limit(options.limit);
    const { data: d, error: e } = await q;
    setData(d || []);
    setError(e);
    setLoading(false);
  }, [table]);

  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, error, refetch: fetch, setData };
}

// ─── Génération rapport IA via Claude API backend ─────────────────────────────
async function genererRapportIA(clientId, clientName, avis) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const res = await fetch(`${API}/api/admin/rapport-ia`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({ clientId }),
    });
    const d = await res.json();
    if (!res.ok) {
      console.error("Erreur rapport IA:", d.error);
      return null;
    }
    return d.rapport || null;
  } catch (e) {
    console.error("Erreur rapport IA:", e);
    return null;
  }
}

// ═════════════════════════════════════════════════════════════════════════════
// LOGIN SCREEN
// ═════════════════════════════════════════════════════════════════════════════
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const { data, error: err } = await supabase.auth.signInWithPassword({ email, password });
    if (err) { setError(err.message); setLoading(false); return; }
    const role = data.user?.user_metadata?.role || "client";
    onLogin({ user: data.user, role });
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: "100vh", background: C.bg,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{ width: 380 }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 32, color: C.olive, fontWeight: 500, letterSpacing: "0.04em" }}>
            UPZ<span style={{ color: C.gold }}>IO</span>
          </div>
          <p style={{ fontSize: 14, color: C.muted, marginTop: 8 }}>Votre réputation Google, en pilote automatique.</p>
        </div>

        <div style={{ ...S.card, padding: "24px 28px" }}>
          <h2 style={{ ...S.sectionTitle, marginBottom: 20, fontSize: 16 }}>Connexion</h2>
          {error && (
            <div style={{ background: C.redBg, color: C.red, padding: "8px 12px", borderRadius: 6, fontSize: 13, marginBottom: 16 }}>
              {error}
            </div>
          )}
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: 14 }}>
              <label style={S.label}>Email</label>
              <input style={S.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={S.label}>Mot de passe</label>
              <input style={S.input} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <Btn variant="gold" style={{ width: "100%", padding: "10px 0", fontSize: 14, justifyContent: "center" }} disabled={loading}>
              {loading ? "Connexion…" : "Se connecter"}
            </Btn>
          </form>
          <p style={{ fontSize: 12, color: C.muted, textAlign: "center", marginTop: 16 }}>
            Problème de connexion ? <span style={{ color: C.gold, cursor: "pointer" }}>contact@upzio.fr</span>
          </p>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// TOPBAR
// ═════════════════════════════════════════════════════════════════════════════
function Topbar({ role, userName, onLogout, systemOk = true }) {
  return (
    <div style={{
      background: C.olive, padding: "0 20px",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      height: 52, position: "sticky", top: 0, zIndex: 100,
    }}>
      <span style={{ fontFamily: "'Syne', sans-serif", color: C.gold2, fontSize: 18, fontWeight: 500, letterSpacing: "0.04em" }}>
        UPZIO
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
        {role === "admin" && (
          <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: systemOk ? "#9FE1CB" : "#F09595" }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: systemOk ? C.green : C.red, display: "inline-block" }} />
            {systemOk ? "Système opérationnel" : "Erreur système"}
          </span>
        )}
        <span style={{ fontSize: 12, color: C.gold }}>{userName}</span>
        <Btn variant="ghost" onClick={onLogout} style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", padding: "4px 8px" }}>
          Déconnexion
        </Btn>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ADMIN DASHBOARD
// ═════════════════════════════════════════════════════════════════════════════
function AdminDash({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedClient, setSelectedClient] = useState(null);

  const tabs = [
    { id: "overview", label: "Vue globale" },
    { id: "clients",  label: "Clients" },
    { id: "avis",     label: "File d'avis" },
    { id: "logs",     label: "Logs" },
    { id: "secu",     label: "Sécurité" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: "'DM Sans', sans-serif" }}>
      <Topbar role="admin" userName="France Zattara" onLogout={onLogout} />

      {/* Tabs */}
      <div style={{ background: C.olive2, display: "flex", padding: "0 20px" }}>
        {tabs.map((t) => (
          <button key={t.id} onClick={() => { setActiveTab(t.id); setSelectedClient(null); }}
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              color: activeTab === t.id ? C.gold2 : "rgba(232,176,64,0.5)",
              padding: "12px 18px",
              cursor: "pointer",
              border: "none",
              borderBottom: activeTab === t.id ? `2px solid ${C.gold2}` : "2px solid transparent",
              background: "transparent",
              transition: "all .15s",
            }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ padding: "16px 20px" }}>
        {activeTab === "overview" && <AdminOverview onSelectClient={(c) => { setSelectedClient(c); setActiveTab("clients"); }} />}
        {activeTab === "clients" && !selectedClient && <AdminClients onSelectClient={setSelectedClient} />}
        {activeTab === "clients" && selectedClient && (
          <ClientDetail client={selectedClient} onBack={() => setSelectedClient(null)} />
        )}
        {activeTab === "avis" && <AdminAvis />}
        {activeTab === "logs" && <AdminLogs />}
        {activeTab === "secu" && <AdminSecu />}
      </div>
    </div>
  );
}

// ── Vue globale ───────────────────────────────────────────────────────────────
function AdminOverview({ onSelectClient }) {
  const { data: clients, loading } = useSupabase("clients", { order: "created_at" });
  const { data: avis } = useSupabase("processed_reviews", { order: "created_at", limit: 100 });

  const actifs = clients.filter((c) => c.active);
  const mrr = actifs.reduce((s, c) => s + (c.plan === "premium" ? 99 : 49), 0);
  const queue = avis.filter((a) => ["scheduled", "pending"].includes(a.status)).length;
  const erreurs = avis.filter((a) => a.status === "error").length;

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        <KpiCard label="Clients actifs" value={actifs.length} sub={`sur ${clients.length} total`} />
        <KpiCard label="MRR" value={`${mrr}€`} sub="abonnements actifs" />
        <KpiCard label="File d'attente" value={queue} sub="scheduled + pending" />
        <KpiCard label="Erreurs" value={erreurs} sub={erreurs > 0 ? "⚠️ à traiter" : "tout est OK"} subColor={erreurs > 0 ? C.red : C.green} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {/* Clients récents */}
        <div style={S.card}>
          <div style={{ ...S.sectionTitle, marginBottom: 12 }}>Clients récents</div>
          {actifs.slice(-5).reverse().map((c) => (
            <div key={c.id} onClick={() => onSelectClient(c)}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "8px 0", borderBottom: `0.5px solid ${C.border}`, cursor: "pointer" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: C.olive }}>{c.business_name}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{c.city} · {fmtDate(c.join_date)}</div>
              </div>
              <Badge variant={c.plan === "premium" ? "premium" : "essential"}>
                {c.plan === "premium" ? "Premium" : "Essentiel"}
              </Badge>
            </div>
          ))}
        </div>

        {/* Avis récents */}
        <div style={S.card}>
          <div style={{ ...S.sectionTitle, marginBottom: 12 }}>Avis récents</div>
          {avis.slice(0, 5).map((a) => (
            <div key={a.id} style={{ display: "flex", justifyContent: "space-between",
              alignItems: "center", padding: "8px 0", borderBottom: `0.5px solid ${C.border}` }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: C.olive }}>{a.review_name}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{stars(a.stars)} · {fmtDate(a.created_at)}</div>
              </div>
              <Badge variant={a.status}>{a.status}</Badge>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Gestion clients ───────────────────────────────────────────────────────────
function AdminClients({ onSelectClient }) {
  const { data: clients, loading, refetch } = useSupabase("clients", { order: "created_at" });
  const [search, setSearch] = useState("");

  const filtered = clients.filter((c) =>
    [c.business_name, c.city, c.sector, c.email].some((v) =>
      (v || "").toLowerCase().includes(search.toLowerCase())
    )
  );

  const toggleActive = async (client) => {
    if (client.active && !window.confirm(`Suspendre ${client.business_name} ?`)) return;
    await supabase.from("clients").update({ active: !client.active }).eq("id", client.id);
    refetch();
  };

  const toggleAuto = async (client, val) => {
    await supabase.from("clients").update({ auto_publish: val }).eq("id", client.id);
    refetch();
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <span style={S.sectionTitle}>Gestion clients ({clients.length})</span>
        <div style={{ display: "flex", gap: 8 }}>
          <input style={{ ...S.input, width: 220 }} placeholder="Rechercher…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <div style={{ background: "#fff", border: `0.5px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <thead style={{ background: C.olive }}>
            <tr>
              {["Établissement", "Offre", "Ville", "Statut", "Google", "Auto-pub", "MRR", "Actions"].map((h, i) => (
                <th key={i} style={{
                  fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 500,
                  color: C.gold, padding: "10px 14px", textAlign: "left",
                  letterSpacing: "0.05em", textTransform: "uppercase",
                  width: ["22%","11%","11%","10%","8%","8%","8%","22%"][i],
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} style={{ borderBottom: `0.5px solid ${C.bg}` }}
                onMouseEnter={(e) => e.currentTarget.style.background = C.bg}
                onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}>
                <td style={{ padding: "11px 14px" }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: C.olive }}>{c.business_name}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{c.sector}</div>
                </td>
                <td style={{ padding: "11px 14px" }}>
                  <Badge variant={c.plan === "premium" ? "premium" : "essential"}>
                    {c.plan === "premium" ? "Premium" : "Essentiel"}
                  </Badge>
                </td>
                <td style={{ padding: "11px 14px", fontSize: 13 }}>{c.city}</td>
                <td style={{ padding: "11px 14px" }}>
                  <Badge variant={c.active ? "active" : "suspended"}>{c.active ? "Actif" : "Suspendu"}</Badge>
                </td>
                <td style={{ padding: "11px 14px", textAlign: "center" }}>
                  <span style={{ color: c.google_connected ? C.green : C.red, fontSize: 16 }}>
                    {c.google_connected ? "✓" : "✗"}
                  </span>
                </td>
                <td style={{ padding: "11px 14px" }}>
                  <Toggle checked={!!c.auto_publish} onChange={(v) => toggleAuto(c, v)} label={`Auto-pub ${c.business_name}`} />
                </td>
                <td style={{ padding: "11px 14px", fontWeight: 500, color: C.olive }}>
                  {c.active ? (c.plan === "premium" ? "99€" : "49€") : "—"}
                </td>
                <td style={{ padding: "11px 14px" }}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Btn onClick={() => onSelectClient(c)} style={{ fontSize: 11, padding: "4px 10px" }}>Voir</Btn>
                    <Btn variant={c.active ? "danger" : "default"} onClick={() => toggleActive(c)}
                      style={{ fontSize: 11, padding: "4px 10px" }}>
                      {c.active ? "Suspendre" : "Réactiver"}
                    </Btn>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: "center", padding: 30, color: C.muted, fontSize: 13 }}>Aucun client trouvé</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Détail client ─────────────────────────────────────────────────────────────
function ClientDetail({ client, onBack }) {
  const [tab, setTab] = useState("info");
  const [rapport, setRapport] = useState(null);
  const [loadingRapport, setLoadingRapport] = useState(false);
  const { data: avis, loading: avisLoading, refetch } = useSupabase("processed_reviews", {
    filter: [["client_id", client.id]],
    order: "created_at",
    limit: 20,
  });

  const genRapport = async () => {
    setLoadingRapport(true);
    const analyse = await genererRapportIA(client.id, client.business_name, avis);
    setRapport(analyse);
    setLoadingRapport(false);
  };

  const updateConfig = async (field, value) => {
    await supabase.from("clients").update({ [field]: value }).eq("id", client.id);
  };

  const validerAvis = async (avisId) => {
    await supabase.from("processed_reviews")
      .update({ status: "scheduled", publish_at: new Date(Date.now() + 15 * 60000).toISOString() })
      .eq("id", avisId);
    refetch();
  };

  const relancerAvis = async (avisId) => {
    await supabase.from("processed_reviews").update({ status: "scheduled", error_msg: null }).eq("id", avisId);
    refetch();
  };

  const statsMois = {
    total: avis.length,
    publiés: avis.filter((a) => a.status === "published").length,
    pending: avis.filter((a) => a.status === "pending").length,
    negatifs: avis.filter((a) => a.is_negative).length,
    noteMoy: avis.length ? (avis.reduce((s, a) => s + (a.stars || 0), 0) / avis.length).toFixed(1) : "—",
  };

  const detailTabs = ["info", "avis", "stripe", "rapport"];

  return (
    <div>
      <button onClick={onBack} style={{ ...S.btn(), marginBottom: 14, display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.muted }}>
        ← Retour aux clients
      </button>

      {/* Header */}
      <div style={{ background: C.olive, borderRadius: 10, padding: "16px 20px", marginBottom: 12,
        display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: 10, background: C.olive2,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "'Syne', sans-serif", fontSize: 16, fontWeight: 500, color: C.gold2 }}>
            {client.business_name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 500, color: C.cream }}>{client.business_name}</div>
            <div style={{ fontSize: 12, color: "#9FE1CB", marginTop: 3 }}>
              {client.sector} · {client.city} · Client depuis le {fmtDate(client.join_date)}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
          <Badge variant={client.plan === "premium" ? "premium" : "essential"}>
            {client.plan === "premium" ? "Premium 99€/mois" : "Essentiel 49€/mois"}
          </Badge>
          <Badge variant={client.active ? "active" : "suspended"}>{client.active ? "Actif" : "Suspendu"}</Badge>
          {client.google_connected && <Badge variant="scheduled">Google connecté</Badge>}
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 12 }}>
        <KpiCard label="Avis reçus" value={statsMois.total} sub="30 derniers jours" />
        <KpiCard label="Publiés" value={statsMois.publiés} sub={`${statsMois.pending} en attente`} subColor={statsMois.pending > 0 ? C.amber : C.green} />
        <KpiCard label="Note moyenne" value={`${statsMois.noteMoy} ★`} sub="sur les avis récents" />
        <KpiCard label="Avis négatifs" value={statsMois.negatifs} sub={statsMois.negatifs > 0 ? "validation recommandée" : "RAS"} subColor={statsMois.negatifs > 0 ? C.red : C.green} />
      </div>

      {/* Sous-tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 14 }}>
        {detailTabs.map((t) => (
          <button key={t} onClick={() => setTab(t)} style={{
            ...S.btn(tab === t ? "gold" : "default"),
            fontSize: 12, padding: "6px 14px", textTransform: "capitalize",
          }}>{t}</button>
        ))}
      </div>

      {/* Onglet Info */}
      {tab === "info" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={S.card}>
            <div style={S.sectionTitle}>Configuration</div>
            {[
              ["Auto-publication", "auto_publish", client.auto_publish],
              ["Validation avis négatifs", "auto_publish_negative", !client.auto_publish_negative],
            ].map(([label, field, val]) => (
              <div key={field} style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "8px 0", borderBottom: `0.5px solid ${C.border}`, fontSize: 13 }}>
                <span style={{ color: C.muted }}>{label}</span>
                <Toggle checked={!!val} onChange={(v) => updateConfig(field, v)} label={label} />
              </div>
            ))}
            {[
              ["Ton", client.tone || "Professionnel"],
              ["Langues", client.plan === "premium" ? "FR / EN / IT" : "FR"],
              ["Alerte email", client.email || "—"],
              ["Alerte SMS", client.plan === "premium" ? (client.phone || "—") : "Non inclus"],
            ].map(([label, val]) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between",
                padding: "8px 0", borderBottom: `0.5px solid ${C.border}`, fontSize: 13 }}>
                <span style={{ color: C.muted }}>{label}</span>
                <span style={{ fontWeight: 500, color: C.olive }}>{val}</span>
              </div>
            ))}
          </div>

          <div style={S.card}>
            <div style={S.sectionTitle}>Contact</div>
            {[
              ["Email", client.email],
              ["Téléphone", client.phone],
              ["Secteur", client.sector],
              ["Ville", client.city],
              ["ID Google", client.google_account_id || "—"],
            ].map(([label, val]) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between",
                padding: "8px 0", borderBottom: `0.5px solid ${C.border}`, fontSize: 13 }}>
                <span style={{ color: C.muted }}>{label}</span>
                <span style={{ fontWeight: 500, color: C.olive, fontSize: 12 }}>{val || "—"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Onglet Avis */}
      {tab === "avis" && (
        <div>
          {avisLoading ? <Spinner /> : avis.map((a) => (
            <div key={a.id} style={S.card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: C.olive }}>{a.review_name}</span>
                    {a.is_negative && <Badge variant="negative">Négatif</Badge>}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                    {stars(a.stars)} · {fmtDate(a.created_at)}
                  </div>
                </div>
                <Badge variant={a.status}>{a.status}</Badge>
              </div>

              {a.review_text && (
                <div style={{ fontSize: 13, color: "#444", lineHeight: 1.5, padding: "8px 12px",
                  background: C.bg, borderRadius: 6, borderLeft: `3px solid ${C.gold}`, marginBottom: 8 }}>
                  {a.review_text}
                </div>
              )}

              {a.response_text && (
                <>
                  <div style={{ ...S.label, marginBottom: 4 }}>Réponse générée</div>
                  <div style={{ fontSize: 13, color: C.olive2, lineHeight: 1.5, padding: "8px 12px",
                    background: C.greenBg, borderRadius: 6, borderLeft: `3px solid ${C.green}` }}>
                    {a.response_text}
                  </div>
                </>
              )}

              {a.status === "pending" && (
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <Btn variant="gold" onClick={() => validerAvis(a.id)} style={{ fontSize: 11, padding: "5px 10px" }}>
                    ✓ Valider & planifier
                  </Btn>
                </div>
              )}
              {a.status === "error" && (
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <Btn onClick={() => relancerAvis(a.id)} style={{ fontSize: 11, padding: "5px 10px" }}>
                    ↺ Relancer
                  </Btn>
                  {a.error_msg && (
                    <span style={{ fontSize: 11, color: C.red, alignSelf: "center" }}>{a.error_msg}</span>
                  )}
                </div>
              )}
            </div>
          ))}
          {!avisLoading && avis.length === 0 && (
            <div style={{ textAlign: "center", padding: 40, color: C.muted, fontSize: 13 }}>Aucun avis pour ce client</div>
          )}
        </div>
      )}

      {/* Onglet Stripe */}
      {tab === "stripe" && (
        <div>
          <div style={S.card}>
            <div style={S.sectionTitle}>Abonnement Stripe</div>
            {[
              ["Statut", <Badge variant={client.active ? "active" : "suspended"}>{client.active ? "Actif" : "Suspendu"}</Badge>],
              ["Plan", <Badge variant={client.plan === "premium" ? "premium" : "essential"}>{client.plan === "premium" ? "Premium 99€/mois" : "Essentiel 49€/mois"}</Badge>],
              ["Prochain renouvellement", "À récupérer via Stripe API"],
              ["Revenu total généré", fmtMoney(client.revenue)],
            ].map(([label, val]) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "9px 0", borderBottom: `0.5px solid ${C.border}`, fontSize: 13 }}>
                <span style={{ color: C.muted }}>{label}</span>
                <span style={{ fontWeight: 500, color: C.olive }}>{val}</span>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <Btn style={{ fontSize: 11 }}>Ouvrir dans Stripe →</Btn>
              <Btn variant="danger" style={{ fontSize: 11 }}
                onClick={async () => {
                  if (window.confirm(`Annuler l'abonnement de ${client.business_name} ?`)) {
                    await supabase.from("clients").update({ active: false }).eq("id", client.id);
                  }
                }}>
                Annuler l'abonnement
              </Btn>
            </div>
          </div>

          <div style={S.card}>
            <div style={S.sectionTitle}>Historique des paiements</div>
            <div style={{ textAlign: "center", padding: 20, color: C.muted, fontSize: 13 }}>
              À connecter via Stripe API (webhook + liste des invoices).<br />
              <span style={{ fontSize: 12 }}>Endpoint : GET /api/admin/stripe/invoices/:clientId</span>
            </div>
          </div>
        </div>
      )}

      {/* Onglet Rapport */}
      {tab === "rapport" && (
        <div>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <Btn variant="gold" onClick={genRapport} disabled={loadingRapport}>
              {loadingRapport ? "Génération IA en cours…" : "Générer le rapport mensuel (IA)"}
            </Btn>
          </div>

          {rapport ? (
            <div style={S.card}>
              <div style={S.sectionTitle}>Rapport — {new Date().toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</div>
              <div style={{ fontSize: 13, color: "#444", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{rapport}</div>
            </div>
          ) : (
            <div style={{ ...S.card, textAlign: "center", padding: 40 }}>
              <div style={{ fontSize: 13, color: C.muted }}>
                Cliquez sur "Générer" pour créer l'analyse IA des avis du mois.<br />
                <span style={{ fontSize: 12 }}>Claude analysera les {avis.length} avis récents et rédigera les tendances en français.</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── File d'avis ───────────────────────────────────────────────────────────────
function AdminAvis() {
  const { data: avis, loading, refetch } = useSupabase("processed_reviews", {
    select: "*, clients(business_name)",
    order: "created_at",
    limit: 50,
  });
  const [filtre, setFiltre] = useState("all");

  const filtered = filtre === "all" ? avis : avis.filter((a) => a.status === filtre);

  const valider = async (id) => {
    await supabase.from("processed_reviews")
      .update({ status: "scheduled", publish_at: new Date(Date.now() + 15 * 60000).toISOString() })
      .eq("id", id);
    refetch();
  };

  const relancer = async (id) => {
    await supabase.from("processed_reviews").update({ status: "scheduled", error_msg: null }).eq("id", id);
    refetch();
  };

  const filtres = [
    { id: "all", label: "Tous" },
    { id: "pending", label: "En attente" },
    { id: "scheduled", label: "Planifiés" },
    { id: "published", label: "Publiés" },
    { id: "error", label: "Erreurs" },
  ];

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <span style={S.sectionTitle}>File d'avis ({filtered.length})</span>
        <Btn onClick={refetch} style={{ fontSize: 11 }}>↺ Actualiser</Btn>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }} translate="no">
        {filtres.map((f) => (
          <button key={f.id} onClick={() => setFiltre(f.id)}
            translate="no"
            style={{
              fontSize: 12, padding: "5px 14px", borderRadius: 20,
              border: `0.5px solid ${C.border}`,
              background: filtre === f.id ? C.olive : "#fff",
              color: filtre === f.id ? C.gold2 : "#444",
              cursor: "pointer", transition: "all .15s",
            }}>
            <span translate="no">{f.label}</span>
            {filtre === f.id && <span translate="no"> ({filtered.length})</span>}
          </button>
        ))}
      </div>

      {filtered.map((a) => (
        <div key={a.id} style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: C.olive }}>{a.review_name}</span>
                {a.is_negative && <Badge variant="negative">Négatif</Badge>}
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                {a.clients?.business_name || "—"} · {stars(a.stars)} · {fmtDate(a.created_at)}
                {a.publish_at && ` · Publication : ${fmtDate(a.publish_at)}`}
              </div>
            </div>
            <Badge variant={a.status}>{a.status}</Badge>
          </div>

          {a.review_text && (
            <div style={{ fontSize: 13, color: "#444", lineHeight: 1.5, padding: "7px 10px",
              background: C.bg, borderRadius: 6, borderLeft: `3px solid ${C.gold}`, marginBottom: 8 }}>
              {a.review_text}
            </div>
          )}

          {a.response_text && (
            <div style={{ fontSize: 13, color: C.olive2, lineHeight: 1.5, padding: "7px 10px",
              background: C.greenBg, borderRadius: 6, borderLeft: `3px solid ${C.green}` }}>
              {a.response_text}
            </div>
          )}

          {a.status === "pending" && (
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <Btn variant="gold" onClick={() => valider(a.id)} style={{ fontSize: 11, padding: "5px 10px" }}>✓ Valider & planifier</Btn>
            </div>
          )}
          {a.status === "error" && (
            <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
              <Btn onClick={() => relancer(a.id)} style={{ fontSize: 11, padding: "5px 10px" }}>↺ Relancer</Btn>
              {a.error_msg && <span style={{ fontSize: 11, color: C.red }}>{a.error_msg}</span>}
            </div>
          )}
        </div>
      ))}

      {filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: 40, color: C.muted, fontSize: 13 }}>
          Aucun avis dans cette catégorie
        </div>
      )}
    </div>
  );
}

// ── Logs ──────────────────────────────────────────────────────────────────────
function AdminLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/admin/logs`)
      .then((r) => r.json())
      .then((d) => { setLogs(d.logs || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const niveauColor = { info: C.blueTxt, warn: C.amber, error: C.red, success: C.green };

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <span style={S.sectionTitle}>Journal système</span>
        <Btn style={{ fontSize: 11 }} onClick={() => window.location.reload()}>↺ Actualiser</Btn>
      </div>

      <div style={{ background: "#fff", border: `0.5px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
        {logs.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: C.muted, fontSize: 13 }}>
            Aucun log disponible — vérifiez l'endpoint /api/admin/logs
          </div>
        ) : logs.map((log, i) => (
          <div key={i} style={{ display: "flex", gap: 16, padding: "9px 16px",
            borderBottom: `0.5px solid ${C.bg}`, fontSize: 12 }}>
            <span style={{ color: C.muted, minWidth: 140, flexShrink: 0 }}>{fmtDate(log.timestamp)}</span>
            <span style={{ color: niveauColor[log.level] || C.muted, minWidth: 50, flexShrink: 0, fontWeight: 500 }}>
              {(log.level || "info").toUpperCase()}
            </span>
            <span style={{ color: "#444", flex: 1 }}>{log.message}</span>
            {log.client && <span style={{ color: C.muted, flexShrink: 0 }}>{log.client}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Sécurité ──────────────────────────────────────────────────────────────────
function AdminSecu() {
  const checks = [
    { label: "HTTPS / TLS (Cloudflare)", status: "ok", note: "Actif" },
    { label: "Row Level Security PostgreSQL", status: "ok", note: "Supabase natif" },
    { label: "OAuth tokens chiffrés AES-256", status: "ok", note: "Implémenté" },
    { label: "Rate limiting 100 req/min", status: "ok", note: "Express middleware" },
    { label: "CORS restreint upzio.fr", status: "ok", note: "Configuré" },
    { label: "Audit logs complets", status: "ok", note: "Actif" },
    { label: "Backup auto Supabase", status: "ok", note: "Quotidien" },
    { label: "2FA admin France", status: "warn", note: "À activer" },
    { label: "Pen test sécurité", status: "warn", note: "Avant 50 clients" },
    { label: "Google OAuth credentials", status: "error", note: "À régénérer — exposés" },
  ];

  const statusMap = { ok: [C.greenBg, C.greenTxt, "✓"], warn: [C.amberBg, C.amberTxt, "⚠"], error: [C.redBg, C.red, "✗"] };

  return (
    <div>
      <div style={S.sectionTitle}>Tableau de bord sécurité</div>
      <div style={{ background: "#fff", border: `0.5px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
        {checks.map((c, i) => {
          const [bg, txt, icon] = statusMap[c.status];
          return (
            <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "11px 16px", borderBottom: `0.5px solid ${C.bg}`, fontSize: 13 }}>
              <span style={{ color: C.olive }}>{c.label}</span>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 12, color: C.muted }}>{c.note}</span>
                <span style={S.badge(bg, txt)}>{icon} {c.status.toUpperCase()}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// PORTAIL CLIENT
// ═════════════════════════════════════════════════════════════════════════════
function ClientPortal({ user, onLogout }) {
  const [tab, setTab] = useState("avis");
  const clientId = user?.user_metadata?.client_id;

  const [clientData, setClientData] = useState(null);

  useEffect(() => {
    if (!clientId) return;
    supabase.from("clients").select("*").eq("id", clientId).single()
      .then(({ data }) => setClientData(data));
  }, [clientId]);

  const { data: avis, loading: avisLoading, refetch } = useSupabase("processed_reviews", {
    filter: [["client_id", clientId]],
    order: "created_at",
    limit: 30,
  });

  const statsMois = {
    total: avis.length,
    publiés: avis.filter((a) => a.status === "published").length,
    pending: avis.filter((a) => a.status === "pending").length,
  };

  const valider = async (id) => {
    await supabase.from("processed_reviews")
      .update({ status: "scheduled", publish_at: new Date(Date.now() + 15 * 60000).toISOString() })
      .eq("id", id);
    refetch();
  };

  const tabs = [
    { id: "avis", label: "Mes avis" },
    { id: "qr", label: "Mon QR code" },
    { id: "compte", label: "Mon abonnement" },
  ];

  const clientName = clientData?.business_name || "Votre établissement";
  const clientSlug = clientName.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "-");

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: "'DM Sans', sans-serif" }}>
      <Topbar role="client" userName={clientName} onLogout={onLogout} />

      <div style={{ padding: "16px 20px" }}>
        {/* Header portail */}
        <div style={{ background: C.olive, borderRadius: 10, padding: "14px 20px", marginBottom: 12,
          display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 16, fontWeight: 500, color: C.cream }}>
              Bonjour {clientData?.contact_name || ""} 👋
            </div>
            <div style={{ fontSize: 12, color: "#9FE1CB", marginTop: 3 }}>
              {clientName} · {clientData?.city} · Offre {clientData?.plan === "premium" ? "Premium" : "Essentiel"}
            </div>
          </div>
          {clientData && (
            <span style={{ ...S.badge(C.gold2, C.olive), fontSize: 13, padding: "5px 12px" }}>
              ★ Note Google
            </span>
          )}
        </div>

        {/* KPIs client */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 14 }}>
          <KpiCard label="Avis reçus" value={statsMois.total} sub="30 derniers jours" />
          <KpiCard label="Réponses publiées" value={statsMois.publiés} sub="automatiquement" />
          <KpiCard label="En attente" value={statsMois.pending}
            sub={statsMois.pending > 0 ? "Votre validation requise" : "Tout est traité"}
            subColor={statsMois.pending > 0 ? C.amber : C.green} />
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4, marginBottom: 14 }}>
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              ...S.btn(tab === t.id ? "gold" : "default"), fontSize: 12, padding: "6px 14px",
            }}>{t.label}</button>
          ))}
        </div>

        {/* Mes avis */}
        {tab === "avis" && (
          <div>
            {avisLoading ? <Spinner /> : avis.map((a) => (
              <div key={a.id} style={S.card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: C.olive }}>{a.review_name}</div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                      {stars(a.stars)} · {fmtDate(a.created_at)}
                    </div>
                  </div>
                  <Badge variant={a.status}>{
                    { published: "Publié", scheduled: "Planifié", pending: "À valider", error: "Erreur" }[a.status] || a.status
                  }</Badge>
                </div>

                {a.review_text && (
                  <div style={{ fontSize: 13, color: "#444", lineHeight: 1.5, padding: "7px 10px",
                    background: C.bg, borderRadius: 6, borderLeft: `3px solid ${C.gold}`, marginBottom: 8 }}>
                    {a.review_text}
                  </div>
                )}

                {a.response_text && (
                  <div style={{ fontSize: 13, color: C.olive2, lineHeight: 1.5, padding: "7px 10px",
                    background: C.greenBg, borderRadius: 6, borderLeft: `3px solid ${C.green}` }}>
                    {a.response_text}
                  </div>
                )}

                {a.status === "pending" && (
                  <div style={{ marginTop: 10 }}>
                    <Btn variant="gold" onClick={() => valider(a.id)} style={{ fontSize: 11, padding: "5px 10px" }}>
                      ✓ Valider cette réponse
                    </Btn>
                  </div>
                )}
              </div>
            ))}
            {!avisLoading && avis.length === 0 && (
              <div style={{ textAlign: "center", padding: 40, color: C.muted, fontSize: 13 }}>
                Aucun avis pour l'instant. Partagez votre QR code pour en recevoir !
              </div>
            )}
          </div>
        )}

        {/* QR Code */}
        {tab === "qr" && (
          <div style={S.card}>
            <div style={S.sectionTitle}>Mon QR code personnalisé</div>
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ width: 140, height: 140, margin: "0 auto 16px", background: C.bg,
                borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center",
                border: `0.5px solid ${C.border}` }}>
                <span style={{ fontSize: 60, color: C.border }}>▦</span>
              </div>
              <div style={{ fontSize: 14, color: C.olive, fontWeight: 500, marginBottom: 6 }}>
                upzio.fr/r/{clientSlug}
              </div>
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 20, maxWidth: 320, margin: "0 auto 20px" }}>
                Ce QR code redirige vos clients directement vers votre fiche Google pour laisser un avis.
                Collez-le sur votre vitrine, vos tables ou votre comptoir.
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                <Btn variant="gold" style={{ fontSize: 13 }}>Télécharger le QR code (PNG)</Btn>
                <Btn style={{ fontSize: 13 }}>Télécharger le sticker (PDF)</Btn>
              </div>
            </div>
          </div>
        )}

        {/* Abonnement */}
        {tab === "compte" && clientData && (
          <div style={S.card}>
            <div style={S.sectionTitle}>Mon abonnement</div>
            {[
              ["Offre", clientData.plan === "premium" ? "Premium — 99€/mois" : "Essentiel — 49€/mois"],
              ["Statut", clientData.active ? "Actif" : "Suspendu"],
              ["Email de contact", clientData.email || "—"],
              ["Téléphone", clientData.phone || "—"],
              ["Rapport mensuel", clientData.plan === "premium" || clientData.plan === "essential" ? "Inclus — envoyé le 1er de chaque mois" : "—"],
            ].map(([label, val]) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "9px 0", borderBottom: `0.5px solid ${C.border}`, fontSize: 13 }}>
                <span style={{ color: C.muted }}>{label}</span>
                <span style={{ fontWeight: 500, color: C.olive }}>{val}</span>
              </div>
            ))}
            <div style={{ marginTop: 14 }}>
              <p style={{ fontSize: 12, color: C.muted }}>
                Pour toute modification d'abonnement, contactez France :{" "}
                <a href="mailto:contact@upzio.fr" style={{ color: C.gold }}>contact@upzio.fr</a>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// APP ROOT
// ═════════════════════════════════════════════════════════════════════════════
export default function App() {
  const [session, setSession] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Charger session existante
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (s) {
        setSession(s);
        setRole(s.user?.user_metadata?.role || "client");
      }
      setLoading(false);
    });

    // Écouter les changements d'auth
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (s) setRole(s.user?.user_metadata?.role || "client");
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleLogin = ({ user, role: r }) => {
    setSession({ user });
    setRole(r);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setRole(null);
  };

  // Fonts Google + bloquer traduction automatique (cause bug removeChild)
  useEffect(() => {
    const link = document.createElement("link");
    link.href = "https://fonts.googleapis.com/css2?family=Syne:wght@400;500&family=DM+Sans:wght@400;500&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    document.documentElement.setAttribute("translate", "no");
    document.documentElement.setAttribute("lang", "fr");
    const meta = document.createElement("meta");
    meta.name = "google";
    meta.content = "notranslate";
    document.head.appendChild(meta);
  }, []);

  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: C.bg, fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 28, color: C.olive, letterSpacing: "0.04em" }}>
          UPZ<span style={{ color: C.gold }}>IO</span>
        </div>
        <div style={{ fontSize: 13, color: C.muted, marginTop: 8 }}>Chargement…</div>
      </div>
    </div>
  );

  if (!session) return <LoginScreen onLogin={handleLogin} />;
  if (role === "admin") return <AdminDash user={session.user} onLogout={handleLogout} />;
  return <ClientPortal user={session.user} onLogout={handleLogout} />;
}
